// The `Client.clientKind` vocabulary and its display labels (ADR-0015 D2).
//
// A Client is the registered holder of accounts, as the book of record styles
// it. `clientKind` DESCRIBES that holder. It is correctable, and nothing
// load-bearing derives from it: the two consumers that branch on it
// (`factFindClientTypeForClientKind` and `clientTypeForClientKind` on the
// server) each choose a FORM, which is a presentation choice and not an
// authority.
//
// The map lives in the contract, because both the web pages and the MCP text
// surface need it, and the contract is the package that both read.

import { z } from 'zod';

/** The settled member set. Each member is one lowercase word. */
export const CLIENT_KINDS = [
    'individual',
    'joint',
    'trust',
    'pension',
    'corporate',
    'charity',
    'internal',
] as const;

export const clientKindSchema = z.enum(CLIENT_KINDS);
export type ClientKind = z.infer<typeof clientKindSchema>;

/**
 * The label that the firm sees. These strings are PRODUCT COPY, so a change to
 * one of them is a product decision (CLAUDE.md).
 *
 * The product owner approved `Joint Account` in place of the run-together
 * `JointAccount` that the pages rendered before. The stored VALUE is unchanged:
 * `joint` is the member, and only its display string carries the space.
 *
 * `internal` is the one new label. No row carried the member before, so the
 * string is a new label and not a changed one.
 */
export const CLIENT_KIND_LABELS: Record<ClientKind, string> = {
    individual: 'Individual',
    joint: 'Joint Account',
    trust: 'Trust',
    pension: 'Pension',
    corporate: 'Corporate',
    charity: 'Charity',
    internal: 'Internal',
};

/** The member set, widened to `string` for the lookup below. A `ReadonlySet<string>`
 *  needs no cast, and a cast is what this repository budgets. */
const CLIENT_KIND_SET: ReadonlySet<string> = new Set(CLIENT_KINDS);

/** True when `value` names a member of `CLIENT_KINDS`. */
export function isClientKind(value: string): value is ClientKind {
    return CLIENT_KIND_SET.has(value);
}

/**
 * The display label of a stored `clientKind`. A value that no member matches
 * renders as it is stored: the resolver does not re-validate a row on read, so
 * a row written before the value migration must stay legible instead of
 * rendering as a blank.
 */
export function clientKindLabel(value: string | null | undefined): string {
    if (value == null || value === '') return '';
    return isClientKind(value) ? CLIENT_KIND_LABELS[value] : value;
}

/** The `{ value, label }` option list that a picker renders, in member order. */
export const CLIENT_KIND_OPTIONS: readonly { value: ClientKind; label: string }[] =
    CLIENT_KINDS.map((value) => ({ value, label: CLIENT_KIND_LABELS[value] }));
